<template>
  <v-card outlined>
    <v-toolbar dense color="grey lighten-2">
      <v-toolbar-title class="subtitle-1"> Units In Zone </v-toolbar-title>
      <v-spacer />
      <span class="caption grey--text mr-3"
        >{{ zoneUnitIds.length }} assigned</span
      >
      <v-btn v-if="!isEditable" outlined small @click="isEditing = true">
        <v-icon small left>fas fa-edit</v-icon>
        Edit
      </v-btn>
      <v-btn v-else outlined small @click="cancelEditing">
        <v-icon small left>fas fa-times-circle</v-icon>
        Cancel
      </v-btn>
    </v-toolbar>
    <v-divider />

    <v-card-text>
      <v-text-field
        v-model="search"
        placeholder="Search all units…"
        dense
        outlined
        clearable
        class="mb-2"
        prepend-inner-icon="mdi-magnify"
      />

      <div v-if="listLoading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary" size="24" />
      </div>
      <div v-else-if="displayRows.length === 0" class="grey--text caption py-2">
        No units found.
      </div>
      <div v-else style="max-height: 320px; overflow-y: auto">
        <v-list dense>
          <template v-for="row in displayRows">
            <v-subheader
              v-if="row.__header"
              :key="row.key"
              class="unit-group-header"
              :class="row.headerClass"
            >
              {{ row.text }}
            </v-subheader>

            <v-list-item
              v-else
              :key="row.id"
              class="unit-row px-2"
              :class="{
                'unit-dimmed': zoneState(row) !== 'this',
                'unit-other-zone': zoneState(row) === 'other',
              }"
            >
              <v-list-item-action class="my-0 mr-3">
                <v-checkbox
                  :input-value="selectedUnitIds.includes(row.id)"
                  :disabled="!isEditable"
                  dense
                  hide-details
                  class="mt-0 pt-0"
                  @change="onToggleUnit(row, $event)"
                />
              </v-list-item-action>

              <span class="font-weight-bold unit-name">{{ row.name }}</span>

              <span class="caption grey--text ml-4 unit-details text-truncate">
                {{ unitDetails(row) }}
              </span>

              <div class="device-cell">
                <v-chip
                  v-if="zoneState(row) === 'other'"
                  x-small
                  label
                  outlined
                  color="orange darken-2"
                  class="font-weight-medium"
                >
                  → {{ otherZoneName(row) }}
                </v-chip>
                <template v-else-if="smartDeviceIds(row).length">
                  <v-chip
                    v-for="id in smartDeviceIds(row)"
                    :key="id"
                    x-small
                    label
                    color="green lighten-5"
                    class="green--text text--darken-2 font-weight-medium"
                  >
                    ✓ {{ id }}
                  </v-chip>
                </template>
                <span v-else class="caption grey--text">— no device</span>
              </div>
            </v-list-item>
          </template>
        </v-list>
      </div>

      <v-btn
        v-if="isEditable"
        depressed
        tile
        small
        color="success"
        :loading="saving"
        class="mt-3"
        @click="saveAssignments"
      >
        Save
      </v-btn>
      <div v-else class="caption grey--text mt-3">
        <v-icon x-small class="mr-1">mdi-lock-outline</v-icon>
        Read-only — click 'Edit' to modify.
      </div>
    </v-card-text>

    <!-- Friction: confirm before pulling a unit out of another zone -->
    <DialogConfirmZoneMove
      :value="!!pendingMoveUnit"
      :unit-name="pendingMoveUnit ? pendingMoveUnit.name : ''"
      :unit-subtitle="moveUnitSubtitle"
      :from-zone="pendingMoveUnit ? otherZoneName(pendingMoveUnit) : ''"
      :to-zone="zoneName || 'this zone'"
      @confirm="confirmMove"
      @cancel="cancelMove"
    />
  </v-card>
</template>

<script>
import useAlerts from "@tod-ui/composables/useAlerts";
import DialogConfirmZoneMove from "../common/DialogConfirmZoneMove.vue";
import {
  GET_ZONE_UNITS,
  GET_FACILITY_UNITS,
  ASSIGN_UNITS_TO_ZONE,
} from "./graphql";
import { UNASSIGN_UNIT_FROM_ZONE } from "../smart_units/graphql";

export default {
  name: "CardUnitsInZone",
  components: {
    DialogConfirmZoneMove,
  },
  props: {
    zoneId: {
      type: String,
      required: true,
    },
    zoneName: {
      type: String,
      default: "",
    },
    facilityId: {
      type: String,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { addAlert } = useAlerts();
    return { addAlert };
  },
  data() {
    return {
      units: [],
      allUnits: [],
      selectedUnitIds: [],
      search: "",
      unitsLoading: false,
      allUnitsLoading: false,
      saving: false,
      isEditing: false,
      // Unit pending a cross-zone move confirmation (friction modal).
      pendingMoveUnit: null,
    };
  },
  computed: {
    isEditable() {
      return this.isEditing || this.editable;
    },
    // Subtitle for the unit shown in the move-confirmation dialog,
    // e.g. "Drive-Up · 10×15 · no device".
    moveUnitSubtitle() {
      if (!this.pendingMoveUnit) return "";
      const n = this.smartDeviceIds(this.pendingMoveUnit).length;
      const deviceLabel = n ? `${n} device${n > 1 ? "s" : ""}` : "no device";
      return [this.unitDetails(this.pendingMoveUnit), deviceLabel]
        .filter(Boolean)
        .join(" · ");
    },
    listLoading() {
      return this.unitsLoading || this.allUnitsLoading;
    },
    // IDs of units currently assigned to this zone (checkbox-independent)
    zoneUnitIds() {
      return this.units.map((u) => u.id);
    },
    // Full pick-list across the whole facility, so a misplaced unit is always
    // visible. Rendered as three groups with headers: this zone (top, checked),
    // then "available to add" (unassigned), then "in other zones" (bottom).
    displayRows() {
      const merged = [...this.units];
      const seen = new Set(this.zoneUnitIds);
      for (const u of this.allUnits) {
        if (!seen.has(u.id)) {
          merged.push(u);
          seen.add(u.id);
        }
      }

      const s = (this.search || "").trim().toLowerCase();
      const match = (u) =>
        !s ||
        u.name.toLowerCase().includes(s) ||
        (u.externalId && u.externalId.toLowerCase().includes(s));
      const byName = (a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true });

      const inGroup = (state) =>
        merged
          .filter((u) => this.zoneState(u) === state && match(u))
          .sort(byName);
      const thisZone = inGroup("this");
      const unassigned = inGroup("unassigned");
      const other = inGroup("other");

      const rows = [];
      if (thisZone.length) {
        rows.push({
          __header: true,
          key: "h-this",
          text: `In this zone — ${thisZone.length} assigned`,
          headerClass: "group-this",
        });
        rows.push(...thisZone);
      }
      if (unassigned.length) {
        rows.push({
          __header: true,
          key: "h-unassigned",
          text: `Available to add — ${unassigned.length} unassigned`,
          headerClass: "group-available",
        });
        rows.push(...unassigned);
      }
      if (other.length) {
        rows.push({
          __header: true,
          key: "h-other",
          text: "In other zones — click in Edit mode to move here",
          headerClass: "group-other",
        });
        rows.push(...other);
      }
      return rows;
    },
  },
  watch: {
    zoneId(newId) {
      if (newId) {
        this.loadZoneUnits();
        this.loadAllFacilityUnits();
      }
    },
  },
  async mounted() {
    await this.loadZoneUnits();
    this.loadAllFacilityUnits();
  },
  methods: {
    // "Climate Control · 10×10" — unit type name + dimensions
    unitDetails(unit) {
      const parts = [];
      if (unit.unitType?.name) parts.push(unit.unitType.name);
      if (unit.unitType?.width && unit.unitType?.length) {
        parts.push(`${unit.unitType.width}×${unit.unitType.length}`);
      }
      return parts.join(" · ");
    },
    // shortIds of the unit's smart (non-camera) devices — a unit can have several
    smartDeviceIds(unit) {
      return (unit.devices || [])
        .filter((d) => d.subtype !== "video_camera")
        .map((d) => d.shortId)
        .filter(Boolean);
    },
    // Group a unit relative to this zone: in this zone / unassigned / in another zone.
    zoneState(unit) {
      if (
        unit.parent?.id === this.zoneId ||
        this.zoneUnitIds.includes(unit.id)
      ) {
        return "this";
      }
      return unit.parent?.subtype === "zone" ? "other" : "unassigned";
    },
    // Name of the other zone a unit currently belongs to (when zoneState === "other").
    otherZoneName(unit) {
      return unit.parent?.name || "another zone";
    },
    // Checkbox handler. Moving a unit out of another zone adds friction (confirm modal);
    // unassigned / this-zone units toggle immediately.
    onToggleUnit(unit, checked) {
      if (checked && this.zoneState(unit) === "other") {
        this.pendingMoveUnit = unit;
        return;
      }
      this.setSelected(unit.id, checked);
    },
    setSelected(unitId, checked) {
      const has = this.selectedUnitIds.includes(unitId);
      if (checked && !has) {
        this.selectedUnitIds = [...this.selectedUnitIds, unitId];
      } else if (!checked && has) {
        this.selectedUnitIds = this.selectedUnitIds.filter(
          (id) => id !== unitId
        );
      }
    },
    confirmMove() {
      if (this.pendingMoveUnit) {
        this.setSelected(this.pendingMoveUnit.id, true);
      }
      this.pendingMoveUnit = null;
    },
    cancelMove() {
      this.pendingMoveUnit = null;
    },
    async loadZoneUnits() {
      if (!this.zoneId) return;
      this.unitsLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: GET_ZONE_UNITS,
          variables: { zoneId: this.zoneId },
          fetchPolicy: "network-only",
        });
        this.units = data?.organizationDescendants || [];
        this.selectedUnitIds = this.units.map((u) => u.id);
        const smartCount = this.units.filter(
          (u) => this.smartDeviceIds(u).length
        ).length;
        this.$emit("units-loaded", {
          total: this.units.length,
          smart: smartCount,
        });
      } catch (e) {
        console.error("CardUnitsInZone: failed to load zone units", e);
      } finally {
        this.unitsLoading = false;
      }
    },

    async loadAllFacilityUnits() {
      if (!this.facilityId) return;
      this.allUnitsLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: GET_FACILITY_UNITS,
          variables: { facilityId: this.facilityId },
          fetchPolicy: "network-only",
        });
        // All facility units (incl. those in other zones), each with its current
        // parent zone so the list can show where a misplaced unit lives.
        this.allUnits = data?.organizationDescendants || [];
        // Surface the facility-wide unit total for the zone summary header.
        this.$emit("units-loaded", { facilityTotal: this.allUnits.length });
        // Pre-check units already assigned to this zone (always keep the full
        // assigned set selected, even if a unit is absent from the candidate
        // list returned by the query).
        this.selectedUnitIds = this.units.map((u) => u.id);
      } catch (e) {
        console.error("CardUnitsInZone: failed to load all facility units", e);
      } finally {
        this.allUnitsLoading = false;
      }
    },

    cancelEditing() {
      this.isEditing = false;
      this.selectedUnitIds = this.units.map((u) => u.id);
    },

    async saveAssignments() {
      this.saving = true;
      try {
        const originalZoneIds = this.units.map((u) => u.id);
        // Units newly checked (coming from unassigned / another zone) → assign here.
        const toAssign = this.selectedUnitIds.filter(
          (id) => !originalZoneIds.includes(id)
        );
        // Units that were in this zone but are now unchecked → unassign (back to facility).
        const toRemove = originalZoneIds.filter(
          (id) => !this.selectedUnitIds.includes(id)
        );

        if (toAssign.length) {
          await this.$apollo.mutate({
            mutation: ASSIGN_UNITS_TO_ZONE,
            variables: { zoneId: this.zoneId, unitIds: toAssign },
          });
        }
        if (toRemove.length) {
          await Promise.all(
            toRemove.map((unitId) =>
              this.$apollo.mutate({
                mutation: UNASSIGN_UNIT_FROM_ZONE,
                variables: { unitId },
              })
            )
          );
        }

        this.addAlert({
          type: "success",
          message: "Zone unit assignments saved.",
          timeout: 10,
        });
        await this.loadZoneUnits();
        await this.loadAllFacilityUnits();
        this.isEditing = false;
        this.$emit("updated");
      } catch (e) {
        console.error("CardUnitsInZone: failed to save assignments", e);
        this.addAlert({
          type: "error",
          message: "Failed to save zone unit assignments.",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Align every row as a consistent grid: checkbox | fixed-width unit name |
   flexible details | right-aligned device column, all vertically centered. */
.unit-row {
  align-items: center;
}
.unit-name {
  flex: 0 0 64px;
  width: 64px;
}
.unit-details {
  flex: 1 1 auto;
  min-width: 0;
}
.device-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-left: 12px;
}
/* Units not in this zone are dimmed; ones already in another zone also get a
   subtle amber left-accent so a misplaced unit stands out in the list. */
.unit-row.unit-dimmed {
  opacity: 0.6;
}
.unit-row.unit-other-zone {
  border-left: 2px solid #ffb300;
}

/* Group section headers between the three unit groups. */
.unit-group-header {
  height: 28px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.unit-group-header.group-this {
  background: #e8f5e9;
  color: #2e7d32;
}
.unit-group-header.group-available {
  background: #f5f5f5;
  color: rgba(0, 0, 0, 0.6);
}
.unit-group-header.group-other {
  background: #fff8e1;
  color: #b9770e;
}
</style>
